import { useMemo } from "react";
import {
	Autocomplete,
	AutocompleteProps,
	Checkbox,
	CircularProgress,
	FormControl,
	FormHelperText,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	SelectChangeEvent,
} from "@mui/material";
import { InputError } from "./types";
import { getMultiSelectAutocompleteInputWidthStyle } from "./selectFieldUtils.ts";
import { AavoTextField } from "./AavoTextField";
import { isTouchDevice } from "src/utils/isTouchDevice";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";
import { CustomPopper, CustomPopperProps } from "src/components/common/popper/CustomPopper.tsx";
import { mergeSx } from "../../../utils/styles.ts";

export interface AavoMultiSelectFieldProps<T, Key extends string | number> extends Omit<
	AutocompleteProps<T, true, false, false>,
	| "onChange"
	| "renderInput"
	| "getOptionKey"
	| "getOptionLabel"
	| "freeSolo"
	| "multiple"
	| "disableClearable"
	| "isOptionEqualToValue"
	| "value"
	| "ref"
	| "slots"
	| "slotProps"
> {
	getOptionKey: (o: T) => Key;
	getOptionLabel: (o: T) => string;
	label: string;
	error?: InputError;
	onChange: (v: Key[]) => void;
	value?: Key[];
	popperProps?: Partial<CustomPopperProps>;
}

export const AavoMultiSelectField = <T, Key extends string | number>(props: AavoMultiSelectFieldProps<T, Key>) => {
	if (isTouchDevice()) return <MultiSelectField {...props} />;
	else return <AutoCompleteMultiSelectField {...props} />;
};

const AutoCompleteMultiSelectField = <T, Key extends string | number>({
	label,
	value: valueProp,
	onChange: onChangeProp,
	options,
	getOptionKey,
	getOptionLabel,
	error,
	autoFocus,
	loading,
	popperProps,
	...other
}: AavoMultiSelectFieldProps<T, Key>) => {
	const [value, setValue] = useMaybeControlledState({
		controlledValue: valueProp,
		onChange: onChangeProp,
		defaultValue: [],
	});
	const optionsForValue = useMemo(
		() =>
			options.filter((o: T) => {
				return value.includes(getOptionKey(o));
			}),
		[options, value, getOptionKey],
	);

	return (
		<Autocomplete
			renderInput={(params) => {
				return (
					<AavoTextField
						{...params}
						fullWidth
						label={label}
						error={error !== undefined}
						helperText={error ?? undefined}
						autoFocus={autoFocus}
						slotProps={{
							htmlInput: {
								...params.slotProps.htmlInput,
								style: {
									...getMultiSelectAutocompleteInputWidthStyle(label, value),
									...params.slotProps.htmlInput.style,
								},
							},
							input: {
								...params.slotProps.input,
								endAdornment: (
									<>
										{loading ?
											<CircularProgress color="inherit" size={20} />
										:	null}
										{params.slotProps.input.endAdornment}
									</>
								),
							},
						}}
					/>
				);
			}}
			options={options}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={(o1, o2) => {
				return getOptionKey(o1) === getOptionKey(o2);
			}}
			multiple={true}
			value={optionsForValue}
			onChange={(_, value) => {
				setValue(value.map(getOptionKey));
			}}
			slots={{
				popper: CustomPopper,
			}}
			slotProps={{
				popper: { placement: "bottom-start" as const, ...popperProps },
			}}
			{...other}
		/>
	);
};

const MultiSelectField = <T, Key extends string | number>({
	label,
	value: valueProp,
	onChange: onChangeProp,
	options,
	getOptionKey,
	getOptionLabel,
	error,
	autoFocus,
	sx,
}: AavoMultiSelectFieldProps<T, Key>) => {
	const [selectedKeys, setSelectedKeys] = useMaybeControlledState<Key[]>({
		controlledValue: valueProp,
		onChange: onChangeProp,
		defaultValue: [],
	});

	const optionsByKey: Map<Key, T> = useMemo(() => {
		const map = new Map<Key, T>();
		options.forEach((o) => map.set(getOptionKey(o), o));
		return map;
	}, [options, getOptionKey]);

	const selectedKeySet: Set<Key> = useMemo(() => new Set(selectedKeys), [selectedKeys]);

	return (
		<FormControl
			error={error !== undefined}
			sx={mergeSx(
				{
					minWidth: 70,
				},
				sx,
			)}
		>
			<InputLabel htmlFor={"multiple-selection-label"} shrink>
				{label}
			</InputLabel>
			<Select<Key[]>
				label={label}
				multiple
				displayEmpty
				value={selectedKeys}
				autoFocus={autoFocus}
				inputProps={{
					id: "multiple-selection-label",
				}}
				onChange={(e: SelectChangeEvent<Key[]>) => {
					const newSelectedKeys = e.target.value;
					// SelectChangeEvent can return string when multiple is true in some edge cases
					if (typeof newSelectedKeys === "string") {
						return;
					}
					setSelectedKeys(newSelectedKeys);
				}}
				renderValue={(selected) => {
					return selected
						.map((key) => {
							const option = optionsByKey.get(key);
							return option ? getOptionLabel(option) : "";
						})
						.filter(Boolean)
						.join(", ");
				}}
			>
				{options.map((o, index) => {
					const key = getOptionKey(o);
					return (
						<MenuItem key={`select-field-option-${index}`} value={key}>
							<Checkbox checked={selectedKeySet.has(key)} size={"small"} />
							<ListItemText primary={getOptionLabel(o)} />
						</MenuItem>
					);
				})}
			</Select>
			<FormHelperText>{error ?? ""}</FormHelperText>
		</FormControl>
	);
};
